import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  VictoryChart,
  VictoryBar,
  VictoryAxis,
  VictoryTheme,
  VictoryTooltip,
} from "victory";
import { useTranslation } from "react-i18next";
import { getColor } from "../Analysis/ScoreChart";
import {
  SubmissionReviewActivity,
  SubmissionReviewSubmission,
} from "../../types";

interface Props {
  activity: SubmissionReviewActivity;
  submissions: SubmissionReviewSubmission[];
  // Drill-down: invoked with the clicked row's submission (results page)
  onStudentClick?: (submission: SubmissionReviewSubmission) => void;
}

export function AggregateResults({
  activity,
  submissions,
  onStudentClick,
}: Props) {
  const [t] = useTranslation();

  // ── Compute per-submission stats ───────────────────────────────────────

  const submissionStats = submissions.map((sub) => {
    const allocations = sub.allocations || [];
    const completedReviews = allocations.filter((a) => a.reviewSubmitted);

    const allScores: number[] = [];
    completedReviews.forEach((alloc) => {
      if (alloc.review?.answers) {
        alloc.review.answers.forEach((ans) => {
          if (ans.value != null) allScores.push(ans.value);
        });
      }
    });

    const avgScore =
      allScores.length > 0
        ? allScores.reduce((a, b) => a + b, 0) / allScores.length
        : null;

    // Outlier detection: flag if avg score is very low or very high
    const isOutlier = avgScore !== null && (avgScore <= 2.0 || avgScore >= 4.8);

    return {
      id: sub.id,
      authorName: sub.authorName,
      name:
        activity.submissionType === "LIV" || activity.submitterMode === "TM"
          ? sub.groupName ?? sub.authorName
          : sub.authorName,
      submissionGrade: sub.grade,
      reviewCount: completedReviews.length,
      totalAllocations: allocations.length,
      avgReviewScore: avgScore,
      isOutlier,
      textFeedbackCount: completedReviews.reduce((count, alloc) => {
        return (
          count + (alloc.review?.answers?.filter((a) => a.text).length ?? 0)
        );
      }, 0),
    };
  });

  // ── Averages by student/team for the overview chart (survey pattern:
  //    who matters at this level, not which criterion) ─────────────────────

  const isLive = activity.submissionType === "LIV";
  const isTeamRows = isLive || activity.submitterMode === "TM";
  const chartData = submissionStats
    .filter((s) => s.avgReviewScore != null)
    .map((s) => ({
      x: s.name,
      y: s.avgReviewScore as number,
      submissionId: s.id,
      label: `${s.name}: ${(s.avgReviewScore as number).toFixed(1)} avg`,
    }))
    .sort((a, b) => a.y - b.y);

  const drillTo = (submissionId: string) => {
    if (!onStudentClick) return;
    const target = submissions.find((sub) => sub.id === submissionId);
    if (target) onStudentClick(target);
  };

  // ── Received comments (peer-feedback results parity): every written
  //    answer, labeled with its rubric criterion ──────────────────────────

  const itemLabels = new Map(
    (activity.rubricItems ?? []).map((item) => [item.id, item.label]),
  );
  const comments = submissions.flatMap((sub) =>
    (sub.allocations ?? []).flatMap((alloc) =>
      (alloc.review?.answers ?? [])
        .filter((ans) => ans.text)
        .map((ans) => ({
          id: ans.id,
          subjectName:
            isTeamRows && sub.groupName ? sub.groupName : sub.authorName,
          reviewerName: alloc.reviewerName,
          criterion: itemLabels.get(ans.itemId) ?? "",
          text: ans.text as string,
        })),
    ),
  );

  // ── Overall statistics ─────────────────────────────────────────────────

  const totalSubmissions = submissions.length;
  const submittedCount = submissions.filter((s) => s.status === "SB").length;
  const totalReviews = submissionStats.reduce(
    (sum, s) => sum + s.reviewCount,
    0,
  );
  const allAvgScores = submissionStats
    .map((s) => s.avgReviewScore)
    .filter((s): s is number => s !== null);
  const overallAvg =
    allAvgScores.length > 0
      ? allAvgScores.reduce((a, b) => a + b, 0) / allAvgScores.length
      : null;
  const outlierCount = submissionStats.filter((s) => s.isOutlier).length;

  // ── DataGrid columns ───────────────────────────────────────────────────

  const columns: GridColDef[] = [
    {
      headerName: String(t(isTeamRows ? "Team" : "Student")),
      field: "name",
      flex: 200,
    },
    {
      headerName: String(t("Grade")),
      field: "submissionGrade",
      flex: 80,
      valueFormatter: ({ value }) =>
        value != null ? Number(value).toFixed(1) : "-",
    },
    {
      headerName: String(t("Reviews")),
      field: "reviewCount",
      flex: 80,
      // FL-284: testers read Reviews vs Feedback as the same thing —
      // spell out what each counts.
      description: String(t("Completed reviews out of assigned reviewers")),
      renderCell: (params: GridRenderCellParams) =>
        `${params.row.reviewCount}/${params.row.totalAllocations}`,
    },
    {
      headerName: String(t("Avg Score")),
      field: "avgReviewScore",
      flex: 80,
      valueFormatter: ({ value }) =>
        value != null ? Number(value).toFixed(1) : "-",
      cellClassName: (params) => (params.row.isOutlier ? "outlier-cell" : ""),
    },
    {
      headerName: String(t("Comments")),
      field: "textFeedbackCount",
      flex: 80,
      description: String(t("Written comments received")),
      valueFormatter: ({ value }) => (value ? String(value) : "-"),
    },
    {
      headerName: String(t("Outlier")),
      field: "isOutlier",
      flex: 60,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? <Warning color="warning" fontSize="small" /> : null,
    },
  ];

  return (
    <Card variant="outlined">
      <CardHeader
        title={String(t("Results Summary"))}
        titleTypographyProps={{ variant: "h6" }}
      />
      <CardContent>
        {/* Overview stats */}
        <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("Submissions")}
            </Typography>
            <Typography variant="h6">
              {submittedCount}/{totalSubmissions}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("Reviews Completed")}
            </Typography>
            <Typography variant="h6">{totalReviews}</Typography>
          </Box>
          {overallAvg !== null && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Average Review Score")}
              </Typography>
              <Typography variant="h6">{overallAvg.toFixed(1)}</Typography>
            </Box>
          )}
          {outlierCount > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Flagged Outliers")}
              </Typography>
              <Typography variant="h6" color="warning.main">
                {outlierCount}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Averages by student/team — mirrors the survey results chart */}
        {chartData.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t(
                isTeamRows
                  ? "Average Score by Team"
                  : "Average Score by Student",
              )}
            </Typography>
            {/* FL-284: tighter rows (26px vs 40px), the peer-feedback score
                gradient, and clickable bars that drill into the student. */}
            <VictoryChart
              horizontal
              theme={VictoryTheme.material}
              padding={{ top: 24, bottom: 42, left: 120, right: 16 }}
              height={Math.max(140, chartData.length * 26 + 66)}
              width={800}
              domain={{ y: [0, 5.5] }}
            >
              <VictoryAxis
                style={{
                  axis: { stroke: "white" },
                  tickLabels: {
                    fill: "white",
                    fontSize: 10,
                  },
                }}
              />
              <VictoryAxis
                dependentAxis
                label={String(t("Avg Score"))}
                style={{
                  axis: { stroke: "white" },
                  axisLabel: { fill: "white", padding: 25, fontSize: 10 },
                  tickLabels: { fill: "white", fontSize: 9 },
                  grid: { stroke: "#333" },
                }}
              />
              <VictoryBar
                barWidth={14}
                labelComponent={<VictoryTooltip constrainToVisibleArea />}
                data={chartData}
                style={{
                  data: {
                    // Non-destructured arg: react/prop-types false-positives
                    // on destructured callbacks inside JSX attributes.
                    fill: (args: { datum?: { y: number } }) =>
                      getColor((args.datum?.y ?? 0) / 5),
                    stroke: "white",
                    strokeWidth: 1,
                    cursor: onStudentClick ? "pointer" : "default",
                  },
                }}
                events={
                  onStudentClick
                    ? [
                        {
                          target: "data",
                          eventHandlers: {
                            // Not named "props": react/prop-types treats a
                            // `props` arg as component props and errors.
                            onClick: (_event, clicked) => {
                              drillTo(clicked.datum.submissionId);
                              return [];
                            },
                          },
                        },
                      ]
                    : []
                }
              />
            </VictoryChart>
          </Box>
        )}

        {/* DataGrid: per-student/team breakdown with outlier flags */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t(isTeamRows ? "Per-Team Breakdown" : "Per-Student Breakdown")}
        </Typography>
        <DataGrid
          autoHeight
          rows={submissionStats}
          columns={columns}
          onRowClick={
            onStudentClick
              ? (params) => drillTo(String(params.row.id))
              : undefined
          }
          hideFooter={submissionStats.length < 100}
          initialState={{
            sorting: { sortModel: [{ field: "avgReviewScore", sort: "desc" }] },
          }}
          sx={{
            backgroundColor: "black",
            mb: 1,
            "& .outlier-cell": {
              color: "#fbc02d",
              fontWeight: "bold",
            },
            ...(onStudentClick && {
              "& .MuiDataGrid-row": { cursor: "pointer" },
            }),
          }}
          getRowHeight={() => "auto"}
          disableSelectionOnClick
        />

        {/* Received comments — same view peer-feedback results offer.
            Instructors always see reviewer identities. */}
        {comments.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("Received Comments")} ({comments.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t(isTeamRows ? "Team" : "Student")}</TableCell>
                  <TableCell>{t("Reviewer")}</TableCell>
                  <TableCell>{t("Criterion")}</TableCell>
                  <TableCell>{t("Comment")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map((comment) => (
                  <TableRow key={comment.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {comment.subjectName}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {comment.reviewerName}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {comment.criterion}
                    </TableCell>
                    <TableCell>{comment.text}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
